import React, { useEffect, useState, useMemo } from "react";
import { Delete, Pen } from "lucide-react";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import CompanyCustomerForm from "./customer_form";
import {
  createCustomer,
  deleteCustomer,
  fetchCustomers,
  updateCustomer,
} from "../../../slices/company/customerSlice";
import { customTableStyles } from "../../../utils/tableStylings";

const CompanyCustomer = () => {
  const dispatch = useDispatch();

  const [showModal, setShowModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [modalHeading, setModalHeading] = useState("");

  const { customers = [] } = useSelector((state) => state.customers);

  // ---------------- FETCH ----------------
  useEffect(() => {
    const company = JSON.parse(localStorage.getItem("company"));
    if (company?.company?.id) {
      dispatch(fetchCustomers(company.company.id));
    }
  }, [dispatch]);

  // ---------------- FILTER EMPTY NAME ----------------
  const filteredCustomers = useMemo(() => {
    return customers.filter(
      (c) => c.name && c.name.trim() !== ""
    );
  }, [customers]);

  // ---------------- FORM SUBMIT ----------------
  const handleFormSubmit = (formData) => {
    const company = JSON.parse(localStorage.getItem("company"));
    const companyId = company?.company?.id;

    if (selectedCustomer?.id) {
      dispatch(updateCustomer({ id: selectedCustomer.id, customerData: formData }));
    } else {
      dispatch(createCustomer({ id: companyId, customerData: formData }));
    }
    setShowModal(false);
  };

  // ---------------- ACTIONS ----------------
  const handleEdit = (row) => {
    setSelectedCustomer(row);
    setModalHeading("Edit Customer");
    setShowModal(true);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This customer will be permanently deleted.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteCustomer(id));
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Customer deleted",
          showConfirmButton: false,
          timer: 3000,
        });
      }
    });
  };

  // ---------------- EXPORT TO EXCEL ----------------
  const exportToExcel = () => {
    if (filteredCustomers.length === 0) {
      Swal.fire("No data to export");
      return;
    }

    const excelData = filteredCustomers.map((c, index) => ({
      "#": index + 1,
      Name: c.name,
      "Company Name": c.company_name,
      Phone: c.phone,
      Email: c.email,
      Type: c.is_wholesaler ? "Wholesaler" : "Retailer",
      "DL No": c.dl_number || c.dl_no || "",
      "Aadhar No": c.aadhar_no || "",
      Address: c.address,
      City: c.city || "",
      State: c.state || "",
      Country: c.country || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Customers");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const file = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(file, "Customers.xlsx");
  };

  // ---------------- TABLE COLUMNS ----------------
  const columns = [
    {
      name: "#",
      cell: (_, index) => index + 1,
      width: "50px",
    },
    {
      name: "Name",
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <div>
          <div className="fw-bold">{row.name}</div>
          {row.company_name && <small className="text-muted">{row.company_name}</small>}
        </div>
      ),
    },
    {
      name: "Type",
      sortable: true,
      cell: (row) => (
        <span className={`badge ${row.is_wholesaler ? 'bg-primary' : 'bg-secondary'}`}>
          {row.is_wholesaler ? 'Wholesaler' : 'Retailer'}
        </span>
      ),
      width: "110px",
    },
    {
      name: "Contact",
      selector: (row) => row.phone || 'N/A',
      sortable: true,
    },
    {
      name: "DL No.",
      selector: (row) => row.dl_number || row.dl_no || 'N/A',
      sortable: true,
    },
    {
      name: "Aadhar No.",
      selector: (row) => row.aadhar_no || 'N/A',
      sortable: true,
    },
    {
      name: "City / State",
      selector: (row) => row.city ? `${row.city}, ${row.state || ''}` : (row.state || 'N/A'),
      sortable: true,
    },
    {
      name: "Actions",
      width: "100px",
      cell: (row) => (
        <div className="d-flex gap-2">
          <span className="text-warning cursor-pointer" onClick={() => handleEdit(row)}>
            <Pen size={18} />
          </span>
          <span className="text-danger cursor-pointer" onClick={() => handleDelete(row.id)}>
            <Delete size={18} />
          </span>
        </div>
      ),
    },
  ];

  // ---------------- ADD ----------------
  const handleAdd = () => {
    setSelectedCustomer(null);
    setModalHeading("Create Customer");
    setShowModal(true);
  };

  // ---------------- RENDER ----------------
  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <div className="d-flex justify-content-between align-items-center">
                <h3 className="text-primary m-0">Customers</h3>

                <div className="d-flex gap-2">
                  <button className="btn btn-success" onClick={exportToExcel}>
                    Export Excel
                  </button>
                  <button className="btn btn-primary" onClick={handleAdd}>
                    + Add Customer
                  </button>
                </div>
              </div>
            </div>
            <div className="card-body">
              {filteredCustomers.length > 0 ? (
                <DataTable
                  columns={columns}
                  data={filteredCustomers}
                  pagination
                  highlightOnHover
                  striped
                  dense
                  customStyles={customTableStyles}
                />
              ) : (
                <p className="text-muted">No Data Found</p>
              )}
            </div>
          </div>
        </div>
      </div>




      <CompanyCustomerForm
        show={showModal}
        onClose={() => setShowModal(false)}
        heading={modalHeading}
        initialData={selectedCustomer}
        onSubmit={handleFormSubmit}
      />
    </>
  );
};

export default CompanyCustomer;
